import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes, TitleStrategy } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { LingPageTitleStrategy } from './core/injectables/ling-page-title-strategy';
import { ExternalRedirectGuard } from './core/guards/external-redirect.guard';
import { AgreementsGuard } from './core/guards/agreements.guard';
import { visitorOnlyCanMatchGuard } from './core/guards/visitor-only.can-match.guard';
import { certificatePrivacyGuard } from './core/guards/certificate-privacy.guard';
import { GlobalWrapperService } from './core/services/global-wrapper.service';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'home',
  },
  {
    path: 'verify',
    pathMatch: 'full',
    redirectTo: 'login',
  },
  {
    path: 'reset-password',
    pathMatch: 'full',
    redirectTo: 'forgot',
  },
  {
    path: 'home',
    title: 'Welcome',
    data: {
      externalUrl: 'https://lingon.io/',
    },
    loadComponent: () =>
      import('./content/public/landing/landing.component').then(
        (c) => c.LandingComponent
      ),
    canActivate: [ExternalRedirectGuard],
  },
  {
    path: 'edit',
    loadChildren: () =>
      import('./content/public/guest-certificate-edit/guest-certificate-edit.module').then(
        (m) => m.GuestCertificateEditModule
      ),
  },
  {
    path: 'certificates',
    loadChildren: () =>
      import('./content/private/certificates/certificates.module').then(
        (m) => m.CertificatesModule
      ),
  },
  {
    path: 'profile',
    title: 'My profile',
    loadChildren: () =>
      import('./content/private/profile/profile.module').then(
        (m) => m.ProfileModule
      ),
    canActivate: [AuthGuard, AgreementsGuard],
  },
  {
    path: 'login',
    title: 'Log-in',
    loadComponent: () =>
      import('./content/public/login/login.component').then(
        (c) => c.LoginComponent
      ),
  },
  {
    path: 'start',
    title: 'Get started',
    canMatch: [visitorOnlyCanMatchGuard],
    loadComponent: () =>
      import('./content/public/start/start.component').then(
        (c) => c.StartComponent
      ),
  },
  {
    path: 'forgot',
    title: 'Forgot password',
    loadChildren: () =>
      import('./content/public/forgot-password/forgot-password.module').then(
        (m) => m.ForgotPasswordModule
      ),
  },
  {
    path: 'terms',
    title: 'Terms of use',
    loadComponent: () =>
      import(
        './content/public/documents/terms-of-use/terms-of-use.component'
      ).then((c) => c.TermsOfUseComponent),
  },
  {
    path: 'privacy',
    title: 'Privacy policy',
    loadComponent: () =>
      import('./content/public/documents/policy/policy.component').then(
        (c) => c.PolicyComponent
      ),
  },
  {
    path: 'contract',
    title: 'Contract terms',
    loadComponent: () =>
      import('./content/public/documents/contract/contract.component').then(
        (c) => c.ContractComponent
      ),
  },
  {
    path: 'download-app',
    title: 'Download Lingon Mobile App',
    loadComponent: () =>
      import(
        './content/public/open-in-mobile-app-redirect-page/open-in-mobile-app-redirect-page.component'
      ).then((c) => c.OpenInMobileAppRedirectPageComponent),
  },
  {
    path: 'invite',
    pathMatch: 'full',
    redirectTo: 'download-app',
  },
  {
    path: 'preview',
    canDeactivate: [
      () => {
        inject(GlobalWrapperService).wrap();
        return true;
      },
    ],
    canActivate: [
      () => {
        inject(GlobalWrapperService).hideWrapper();
        return true;
      },
    ],
    children: [
      {
        path: '',
        title: 'Certificate preview',
        loadComponent: () =>
          import(
            './content/public/certificate-preview/certificate-preview.component'
          ).then((c) => c.CertificatePreviewComponent),
      },
      {
        path: ':id',
        title: 'Certificate preview',
        canActivate: [certificatePrivacyGuard],
        loadComponent: () =>
          import(
            './content/public/certificate-preview/certificate-preview.component'
          ).then((c) => c.CertificatePreviewComponent),
      },
    ],
  },
  {
    path: 'redirect',
    title: 'Redirect',
    loadComponent: () =>
      import('./content/public/redirect/redirect.component').then(
        (c) => c.RedirectComponent
      ),
  },
  {
    path: '404',
    title: 'Oops :(',
    loadComponent: () =>
      import('./content/public/not-found/not-found.component').then(
        (c) => c.NotFoundComponent
      ),
  },
  {
    path: 'certificate-private',
    title: 'Certificate is private',
    canActivate: [
      () => {
        inject(GlobalWrapperService).wrap();
        return true;
      },
    ],
    loadComponent: () =>
      import('./content/public/certificate-private/certificate-private.component').then(
        (c) => c.CertificatePrivateComponent
      ),
  },

  {
    path: 'media-library',
    loadChildren: () =>
      import('./content/private/media-library/media-library.module').then(
        (m) => m.MediaLibraryModule
      ),
  },
  {
    path: '**',
    redirectTo: '404',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [
    {
      provide: TitleStrategy,
      useClass: LingPageTitleStrategy,
    },
  ],
})
export class AppRoutingModule {}
